<template>
    <div class="contenedor-principal">
        <h1>Bombas de semillas: pequeñas bolitas que esconden vida</h1>

        <p>Con un poco de arcilla, compost y semillas podemos preparar en un rato una herramienta sencilla para llenar de
            flores un rincón degradado. Las <strong>bombas de semillas</strong> son también una de las formas más
            divertidas de acercarnos al cuidado de la flora local, a cualquier edad.</p>

        <h2>¿Qué son?</h2>
        <p>Son mezclas de <strong>arcilla, compost o tierra fértil y semillas</strong>, formadas en pequeñas bolitas. La
            arcilla protege las semillas hasta que llega la lluvia: entonces se ablanda y, con la humedad y las
            temperaturas adecuadas, las semillas germinan y pueden devolver la vida a espacios degradados o vacíos.</p>

        <h2>Un poco de historia</h2>
        <p>La idea no es nueva: suele describirse como una técnica antigua, aunque hay pocas fuentes históricas que
            permitan situar su origen con precisión.</p>

        <p>Quien la dio a conocer en el siglo XX fue el agricultor japonés <strong>Masanobu Fukuoka</strong>, pionero de
            la agricultura natural, con el nombre de <em>nendo dango</em>. Su libro <em>La revolución de una brizna de
            paja</em> (1975) difundió su manera de cultivar sin labrar la tierra. Hoy las bombas de semillas se usan en
            proyectos de restauración ecológica, educación ambiental y arte verde.</p>

        <h2>Cómo se hacen</h2>
        <ol>
            <li>Mezcla 3 partes de arcilla, 2 partes de compost y 1 parte de semillas.</li>
            <li>Añade agua poco a poco hasta obtener una masa maleable.</li>
            <li>Forma bolitas de unos 2-3 cm de diámetro.</li>
            <li>Déjalas secar a la sombra durante 1 o 2 días.</li>
            <li>Guárdalas en un lugar fresco y seco hasta el momento de lanzarlas o colocarlas.</li>
        </ol>

        <h3>Elige bien los ingredientes</h3>
        <p><strong>Semillas:</strong> usa especies silvestres locales y evita las ornamentales y las exóticas. Una buena referencia es combinar especies de vida corta
            (amapola, zanahoria silvestre) con perennes (milenrama, margarita mayor, cuernecillo), para asegurar una
            floración continua y una estructura a largo plazo.</p>

        <p><strong>Sustrato:</strong> emplea arcilla, compost y tierra de la zona, de modo que las semillas queden bien
            protegidas y en contacto con el suelo al lanzarlas.</p>

        <h2>Dónde y cuándo lanzarlas</h2>
        <p>Úsalas en <strong>lugares degradados o zonas acordadas</strong>, siempre con permiso de la propiedad o del
            ayuntamiento, y nunca en espacios protegidos ni en prados o bosques en buen estado. Algunos buenos
            candidatos son los bordes de caminos, los solares abandonados, los jardines comunitarios y las zonas verdes
            urbanas con algo de luz solar.</p>

        <p>El mejor momento es la <strong>primavera (marzo-mayo) o el otoño (septiembre-octubre)</strong>, cuando hay
            humedad y las temperaturas son suaves, evitando las heladas fuertes.</p>

        <h2>¿Por qué hacerlas?</h2>
        <p>Aunque es una actividad sencilla, reúne varios aspectos del cuidado del medioambiente:</p>
        <ul>
            <li><strong>Favorecen la biodiversidad local:</strong> atraen polinizadores como abejas, mariposas y abejorros,
                y dan un empujón a la <router-link to="/blog/flora-autoctona">flora autóctona</router-link>.</li>
            <li><strong>Son educación ambiental:</strong> se preparan en talleres sencillos, rápidos y divertidos para todas
                las edades, que nos conectan con nuestro entorno.</li>
            <li><strong>Reverdecen sin excavar:</strong> si usamos semillas de la zona, podemos sembrar respetando el suelo y
                sin necesidad de maquinaria.</li>
            <li><strong>Sirven para la restauración ligera:</strong> resultan prácticas en zonas verdes urbanas y
                periurbanas.</li>
        </ul>

        <h2>¿Funcionan siempre?</h2>
        <p>No. La arcilla protege las semillas y algunos estudios han observado que ayuda a que broten antes, pero eso no
            siempre se traduce en más plantas a largo plazo. El resultado depende de las especies, del clima, de la mezcla
            y del lugar. Por eso conviene volver a la zona con el tiempo y ver qué ha crecido, algo que también forma parte
            del aprendizaje.</p>

        <!-- CAJA DE RECURSOS -->
        <div class="caja-recurso">
            <h3>Hazlas con nosotros</h3>
            <p>
                Hemos preparado bombas de semillas en talleres en Getxo, Sopela, Güeñes y Zalla, donde también las hemos
                lanzado junto a la ciudadanía dentro de <router-link to="/detalle-zalla-natura">Zalla Natura</router-link>.
                Antes de elegir tus semillas, puedes conocer mejor nuestra flora en la guía de fichas etnobotánicas.
            </p>
            <div class="botones-recurso">
                <router-link to="/calendario" class="proximas-actividades">Ver próximas actividades</router-link>
                <router-link to="/fitxas-etnobotanicas-enkarterri" class="btn-ver-mas">Conoce nuestra flora</router-link>
                <router-link to="/contacto?servicio=talleres_municipios" class="btn-ver-mas">Pedir un taller</router-link>
            </div>
            <p class="descargas-titulo"><strong>Lámina para imprimir:</strong></p>
            <div class="botones-recurso">
                <a href="/descargas/laminas/bombas-semillas-es.pdf" download class="btn-ver-mas">Castellano (PDF)</a>
                <a href="/descargas/laminas/bombas-semillas-eu.pdf" download class="btn-ver-mas">Euskera (PDF)</a>
            </div>
        </div>

        <!-- REDES SOCIALES -->
        <p class="social-media"><b>¡Síguenos en redes sociales para descubrir más curiosidades sobre la naturaleza de
                nuestro entorno!</b></p>
        <div class="redes-sociales">
            <a href="https://www.instagram.com/geotxiki/" target="_blank" rel="noopener noreferrer"
                title="Geobizi en Instagram">
                <img src="/imagenes/iconos/instagram.png" alt="Geobizi en Instagram" class="social-icon"
                    loading="lazy" />
            </a>
            <a href="https://www.facebook.com/geobizirik/" target="_blank" rel="noopener noreferrer"
                title="Geobizi en Facebook">
                <img src="/imagenes/iconos/facebook.png" alt="Geobizi en Facebook" class="social-icon" loading="lazy" />
            </a>
            <a href="https://www.tiktok.com/@geobizi" target="_blank" rel="noopener noreferrer"
                title="Geobizi en TikTok">
                <img src="/imagenes/iconos/tiktok.png" alt="Geobizi en TikTok" class="social-icon" loading="lazy" />
            </a>
        </div>

        <!-- GALERÍA DE IMÁGENES -->
        <div class="imagen-trio">
            <div>
                <img class="img" src="/imagenes/blog/detalle/bombassemillas1.avif"
                    alt="Manos de una niña y de una persona adulta preparando la mezcla de bombas de semillas"
                    title="Preparando la mezcla" loading="lazy" />
            </div>
            <div>
                <img class="img" src="/imagenes/blog/detalle/bombassemillas2.avif"
                    alt="Manos dando forma a las bolitas sobre una bandeja de taller"
                    title="Dando forma a las bombas" loading="lazy" />
            </div>
            <div>
                <img class="img" src="/imagenes/actividades/bombassemillas1.avif"
                    alt="Bomba de semillas terminada sobre la palma de una mano"
                    title="Bomba de semillas terminada" loading="lazy" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

const pageUrl = 'https://www.geobizi.com/blog/bombas-de-semillas'
const ogImage = 'https://www.geobizi.com/imagenes/blog/detalle/bombassemillas1.avif'

useHead({
    title: 'Bombas de semillas: qué son y cómo hacerlas | Geobizi',
    meta: [
        { name: 'description', content: 'Aprende qué son las bombas de semillas o nendo dango, cómo hacerlas paso a paso, qué semillas elegir y dónde y cuándo lanzarlas de forma responsable.' },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Geobizi' },
        { name: 'theme-color', content: '#0b8a4c' },
        { name: 'language', content: 'es' },
        { property: 'og:title', content: 'Bombas de semillas: pequeñas bolitas que esconden vida' },
        { property: 'og:description', content: 'Qué son, cómo se hacen y cómo usarlas de forma responsable para favorecer la flora local.' },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:image', content: ogImage },
        { name: 'twitter:card', content: 'summary_large_image' }
    ],
    link: [
        { rel: 'canonical', href: pageUrl },
        { rel: 'image_src', href: ogImage }
    ],
    script: [
        {
            type: 'application/ld+json',
            children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": "Bombas de semillas: pequeñas bolitas que esconden vida",
                "description": "Qué son las bombas de semillas, cómo se hacen y cómo usarlas de forma responsable para favorecer la flora local.",
                "image": ogImage,
                "author": {
                    "@type": "Organization",
                    "name": "Geobizi",
                    "url": "https://www.geobizi.com"
                },
                "publisher": {
                    "@type": "Organization",
                    "name": "Geobizi",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
                    }
                },
                "datePublished": "2026-11-05T08:00:00+01:00",
                "dateModified": "2026-11-05T08:00:00+01:00",
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": pageUrl
                }
            })
        }
    ]
})
</script>

<style scoped>
/* PASOS NUMERADOS (el reset global quita los números) */
ol {
    margin: 1rem 1rem 1rem 2.5rem;
}

ol li {
    list-style: decimal;
    margin-left: 0;
}

/* GALERÍA: mismas proporciones aunque las fotos sean distintas */
.imagen-trio .img {
    aspect-ratio: 4 / 3;
    object-fit: cover;
}

/* CAJA DE RECURSOS */
.caja-recurso {
    background-color: var(--megashoftgreen);
    border-left: 4px solid var(--shoftgreen);
    padding: 1.5rem;
    margin: 2.5rem 0;
    border-radius: 0 0.5rem 0.5rem 0;
}

.caja-recurso h3 {
    margin-top: 0;
    color: var(--lightgreen);
    font-size: 1.2rem;
}

.caja-recurso p {
    margin-bottom: 1.5rem;
    color: var(--darkgrey);
}

.caja-recurso .descargas-titulo {
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
}

.botones-recurso {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.proximas-actividades {
    background-color: var(--supershoftgreen);
    color: var(--lightgreen);
    padding: 0.6rem 1.2rem;
    text-decoration: none;
    font-weight: bold;
    border-radius: 4px;
    transition: background-color 0.3s;
}

.proximas-actividades:hover {
    background-color: var(--shoftgreen);
    color: var(--white);
}

.btn-ver-mas {
    background-color: transparent;
    color: var(--lightgreen);
    padding: 0.6rem 1.2rem;
    text-decoration: none;
    font-weight: bold;
    border: 1px solid var(--shoftgreen);
    border-radius: 4px;
    transition: all 0.3s;
}

.btn-ver-mas:hover {
    background-color: var(--shoftgreen);
    color: white;
}

/* REDES SOCIALES */
.social-media {
    margin-top: 2rem;
    color: var(--darkgrey);
}

.redes-sociales {
    margin-left: 1rem;
    display: flex;
    align-items: center;
}

.social-icon {
    width: 2.5rem;
    margin-right: 1rem;
    margin-bottom: 1.5rem;
    transition: transform 0.3s;
}

.social-icon:hover {
    transform: scale(1.1);
}
</style>
